import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Send, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [team, setTeam] = useState([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [photoErrors, setPhotoErrors] = useState({});

  // Default team data as fallback
  const defaultTeam = [
    {
      name: 'Alexandra CHALLET',
      phone: '06 29 65 35 24',
      role: 'Scénariste, Réalisatrice d\'action & documentaire',
      specialty: '2ème équipe action, Actrice',
      photo_url: '/api/files/78c9ecc4-6538-400c-90c8-5f1a9b9bf1ae',
      initials: 'AC',
      color: '#FACC15',
    },
    {
      name: 'André FERRER',
      phone: '07 68 25 76 00',
      role: 'Comédien cascadeur',
      specialty: 'Régleur cascade',
      photo_url: '/api/files/9108db61-46d6-4015-bd0d-77cc2317e4ab',
      initials: 'AF',
      color: '#22C55E',
    },
    {
      name: 'Éric TATIN',
      phone: '07 86 12 39 12',
      role: 'Régleur cascade',
      specialty: 'Spécialisé câblage',
      photo_url: '/api/files/32047b07-17f9-4892-90e3-ae2d53eeb014',
      initials: 'ET',
      color: '#3B82F6',
    },
  ];

  const fetchTeam = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/team`);
      if (response.data && response.data.length > 0) {
        const teamWithFallbacks = response.data.map((member, idx) => ({
          ...member,
          initials: member.name.split(' ').map(n => n[0]).join(''),
          color: ['#FACC15', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899'][idx % 5],
        }));
        setTeam(teamWithFallbacks);
      } else {
        setTeam(defaultTeam);
      }
    } catch (error) {
      setTeam(defaultTeam);
    } finally {
      setTeamLoading(false);
    }
  }, [defaultTeam]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/messages`, formData);
      setSubmitted(true);
      toast.success('Message envoyé avec succès !');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Header */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
              Parlons ensemble
            </p>
            <h1 
              className="font-['Teko'] text-6xl md:text-8xl text-white mb-6"
              data-testid="contact-title"
            >
              CONTACT
            </h1>
            <p className="font-['Barlow'] text-gray-400 text-lg max-w-2xl mx-auto">
              Une question, un projet ? N'hésitez pas à nous contacter. 
              Notre équipe vous répondra dans les plus brefs délais.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="pb-16">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-[#09090b] border border-white/10 p-8 hover:border-[#FACC15]/30 transition-colors"
                data-testid={`team-contact-${index}`}
              >
                {/* Photo en format iPhone (portrait) avec fallback initiales */}
                <div className="aspect-[9/16] w-32 mb-6 overflow-hidden bg-[#18181b]">
                  {!photoErrors[member.name] && (member.photo_url || member.photo) ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${member.photo_url || member.photo}`}
                      alt={member.name}
                      className="w-full h-full object-cover"
                      onError={() => setPhotoErrors(prev => ({...prev, [member.name]: true}))}
                    />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center text-black font-['Teko'] text-4xl font-bold"
                      style={{ backgroundColor: member.color }}
                    >
                      {member.initials}
                    </div>
                  )}
                </div>
                <h3 className="font-['Teko'] text-2xl text-white uppercase mb-1">
                  {member.name}
                </h3>
                <p className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-wider mb-1">
                  {member.role}
                </p>
                <p className="font-['Barlow'] text-gray-400 text-sm mb-4">
                  {member.specialty}
                </p>
                <a 
                  href={`tel:${member.phone.replace(/\s/g, '')}`}
                  className="inline-flex items-center gap-2 text-white hover:text-[#FACC15] transition-colors font-['Barlow']"
                >
                  <Phone size={16} className="text-[#FACC15]" />
                  {member.phone}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Email */}
      <section className="pb-16">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-[#18181b] border border-white/10 p-8 text-center"
          >
            <Mail size={32} className="text-[#FACC15] mx-auto mb-4" />
            <h3 className="font-['Teko'] text-2xl text-white uppercase mb-2">
              Email professionnel
            </h3>
            <a 
              href="mailto:contact@stuntdirector.fr"
              className="font-['Barlow'] text-[#FACC15] text-xl hover:underline"
              data-testid="contact-email"
            >
              contact@stuntdirector.fr
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="font-['Teko'] text-4xl text-white text-center mb-8 uppercase">
              Envoyez-nous un message
            </h2>

            {submitted ? (
              <div className="bg-[#09090b] border border-[#FACC15]/30 p-12 text-center">
                <div className="w-16 h-16 bg-[#FACC15] mx-auto mb-6 flex items-center justify-center">
                  <Check size={32} className="text-black" />
                </div>
                <h3 className="font-['Teko'] text-3xl text-white mb-4">
                  MESSAGE ENVOYÉ !
                </h3>
                <p className="font-['Barlow'] text-gray-400">
                  Merci pour votre message. Nous vous répondrons rapidement.
                </p>
              </div>
            ) : (
              <form 
                onSubmit={handleSubmit}
                className="bg-[#09090b] border border-white/10 p-8 space-y-6"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                      Votre nom
                    </Label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-black border-white/20 focus:border-[#FACC15] text-white rounded-none"
                      placeholder="Nom complet"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                      Email
                    </Label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-black border-white/20 focus:border-[#FACC15] text-white rounded-none"
                      placeholder="votre@email.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Sujet
                  </Label>
                  <Input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="bg-black border-white/20 focus:border-[#FACC15] text-white rounded-none"
                    placeholder="Objet de votre message"
                    data-testid="input-subject"
                  />
                </div>

                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Message
                  </Label>
                  <Textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="bg-black border-white/20 focus:border-[#FACC15] text-white rounded-none min-h-[150px]"
                    placeholder="Votre message..."
                    data-testid="input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-widest py-6 hover:bg-[#EAB308] transition-colors"
                  data-testid="submit-contact"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="spinner" />
                      Envoi...
                    </span>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Envoyer le message
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
